/**
*+------------------------------------------------------------------------+
*| Licensed Materials - Property of IBM
*| IBM Cognos Products: Dashboard
*| (C) Copyright IBM Corp. 2017, 2021
*|
*| US Government Users Restricted Rights - Use, duplication or disclosure
*| restricted by GSA ADP Schedule Contract with IBM Corp.
*+------------------------------------------------------------------------+
*/
define([
	'jquery',
	'../common/EventTarget',
	'../../../apiHelpers/SlotAPIHelper',
	'underscore',
	'../VisEventHandler',
	'../../vipr/VIPRUtils',
	'../../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'../../vipr/data/VIPRCatContDataItem',
	'./VIPRDataPointSummarizer'
], function(
	$,
	EventTarget,
	SlotAPIHelper,
	_,
	VisEventHandler,
	VIPRUtils,
	BrowserUtils,
	VIPRCatContDataItem,
	VIPRDataPointSummarizer
) {

	const ZOOM_VELOCITY = 0.002;
	const TOUCHMARGIN = 1;

	const VIPRTYPE = {
		CUSTOMDATA: 'customdata',
		DATAPOINT: 'datapoint',
		ITEMCLASS: 'itemclass',
		ITEM: 'item',
		DATAITEM: 'dataitem',
		TUPLE: 'tuple',
		VALUE: 'value',
		INVALID: null,
		EMPTY: 'empty'
	};

	const CUSTOMDATATYPE = {
		KEYDRIVERSHOW: 'keydriver_show',
		DRIVERINFO: 'driverinfo',
		TREENODEINFO: 'treenodeinfo'
	};

	const VIPRDATAPOINTTYPE = {
		VALUE: 'value',
		TUPLE: 'tuple'
	};

	class VIPREventTarget extends EventTarget {
		constructor (...args) {
			super(...args);

			const options = args[0] || {};
			this.viprWidget = options.viprWidget;
			this.viprInteractivity = this.viprWidget.getInteractivity();

			// map from visDecorations to viprDecoration
			this.viprDecorations = {};
			this.viprDecorations[VisEventHandler.DECORATIONS.HIGHLIGHT] = 'highlighted';
			this.viprDecorations[VisEventHandler.DECORATIONS.SELECT] = 'selected';
			this.viprDecorations[VisEventHandler.DECORATIONS.TOOLTIP] = 'tooltip';

			this.viprDataPointSummarizer = new VIPRDataPointSummarizer(this.visualization);
			return this.getAPI();
		}

		/**
		 * remove viprWidget from event
		 */
		remove() {
			this.viprWidget = null;
			super.remove();
		}
		/**
		 * @override
		 * Determine whether visualization state (ie. zoom, pan, scroll) can be restored
		 * @return {boolean} true if the visualization state can be restored, otherwise false
		 */
		canRestore() {
			// state can be restore if
			// 1. interactivityState property is supportedAND
			// 2. autoZoom is disabled
			return VIPRUtils.isPropertyIncluded(this.visualization.getDefinition().getId(), 'interactivityState') && !this.content.getPropertyValue('autoZoom');
		}

		/**
		 * @override
		 * Restore the visualization state
		 * @param {Object} state object containing the state of the visualization
		 */
		restore (state) {
			if (state && this.canRestore()) {
				this.viprWidget.setState(state);
			}
		}

		/**
		 * Get the array of event targets based on the event.
		 * Event target represents a data point object that corresponds to the event.
		 * NOTES:
		 *   Legend Titles are of type VIPRTYPE.DATAITEM which contain entries of type VIPRTYPE.TUPLE (note: 1 DATAITEM/TUPLE is returned for stacked legends)
		 *	 Axis Titles are of type VIPRTYPE.ITEMCLASS which contain entries of type VIPRTYPE.ITEM (note: n ITEMCLASSES/ITEMS are returned for stacked axes.)
		 *	 Chart elements (eg bubbles) are of type VIPRTYPE.DATAPOINT
		 *
		 * @param event	Event object
		 */
		getEventTargets (event) {
			let clientX = this._getClientX(event);
			let clientY = this._getClientY(event);

			let visCoord = this.viprWidget.getVisCoordinate(clientX, clientY);
			let items = this.viprWidget.getItemsAtPoint(visCoord);

			// for mobile events, we make a square around the event coords and return the items inside the square
			// if nothing was found - consider moving the isIPad check to visEventHandler in the future.
			if ((!items || items.length === 0) && BrowserUtils.isIPad()) {
				let coords = this._inflateCoordinates(clientX, clientY);
				return this.getTargetsByCords(coords, false);
			} else {
				let options = {
					visCoord: visCoord
				};
				return this._processItems(items, options);
			}
		}

		_inflateCoordinates (clientX, clientY) {
			return [
				{ x: clientX - TOUCHMARGIN, y: clientY - TOUCHMARGIN },
				{ x: clientX + TOUCHMARGIN, y: clientY - TOUCHMARGIN },
				{ x: clientX + TOUCHMARGIN, y: clientY + TOUCHMARGIN },
				{ x: clientX - TOUCHMARGIN, y: clientY + TOUCHMARGIN }
			];
		}

		getTargetsByCords (cords, summarize){
			var targets;
			var visCords = cords.map(function(cord){
				return this.viprWidget.getVisCoordinate(cord.x, cord.y);
			}.bind(this));
			var items = this.viprWidget.getItemsInPolygon(visCords);
			var options = {
				summarize: summarize,
				visCoord: visCords
			};
			targets = this._processItems(items, options);
			return targets;
		}

		/**
		 * @override
		 * Get the axis items from event and offset
		 * @param event - the event as generated by visEventHandler
		 * @param offset - an optional offset (in pixels) to ensure the line does not become the target of selections etc.
		 * @return {object} The axis items
		 */
		getValuesAtPoint (event, offset){
			if(this.viprWidget && this.viprWidget.getVisCoordinate){
				const eventOffset = offset || 0;
				const coord = this.viprWidget.getVisCoordinate(event.clientX + eventOffset, event.clientY - eventOffset);
				return this.viprWidget.getAxisItemsAtPoint(coord);
			}
		}

		/**
		 * Visually decorate the targets.
		 *
		 * @param targets				Event targets
		 * @param visDecorationName		Decoration name (ie. highlight, select)
		 * @return true if target has been decorated, otherwise false
		 */
		decorateTarget (targets, visDecorationName, value) {
			let decorated = false;
			const viprDecorationName = this.viprDecorations[visDecorationName];
			if (viprDecorationName) {
				_.each(targets, function(target) {
					if (target.source && typeof target.source.decorate === 'function') {
						target.source.decorate(viprDecorationName, value);
						if(visDecorationName === 'highlight') {
							target.source.decorate('text', value);
						}
						decorated = true;
					} else if (target.type === 'customdata') {
						// custom data decoration is handled by applyCustomDataSelection
						decorated = true;
					}
				});
			}
			return decorated;
		}

		/**
		 * Obtain the decoration value from the given target item and decoration name
		 * @param item	event target item containing the decoration
		 * @param decorationName common decoration name defined by VisEventHandler.DECORATIONS
		 */
		getDecoration (item, decorationName) {
			const viprDecorationName = this.viprDecorations[decorationName];
			if (viprDecorationName &&
				item.source &&
				typeof item.source.hasDecoration === 'function' &&
				typeof item.source.getDecoration === 'function' &&
				item.source.hasDecoration(viprDecorationName)) {
				return item.source.getDecoration(viprDecorationName);
			}
		}

		/**
		 * Complete the series of decorations.
		 * After clearing and adding multiple decorations, complete the decorations by rendering the visualization.
		 */
		completeDecorations () {
			this.viprWidget.render('client');
		}

		/**
		 * Apply selection on customdata target.
		 *
		 * @param {ArrayOfObject} targets	Event targets
		 */
		applyCustomDataSelection (targets, options) {
			const target = _.isArray(targets) ? targets[0] : targets; //Sample the targets
			// target is empty when all decorations needs to be cleared
			// otherwise the decorating target item type needs to be customdata
			if (!target || (target.type === VIPRTYPE.CUSTOMDATA && !this._isLengendAndEncodingPayload(target))) {
				//todo: need to make custom data decoration consistent with other data;
				this._handleCustomDataDecorations(targets, options);
			}
		}

		//TODO: post Endor R1, we will remove this check and re-enable Comet Chart Legend integration
		_isLengendAndEncodingPayload (item){
			if(item.area === 'legend' && item.values && item.values.length === 1){
				const payload = item.values[0].payload;
				return payload && payload.type === 'encoding';
			}
			return false;
		}

		/**
		 * Return the list of custom data selections in label/value pair
		 * @param {Object} target event target item of the selected custom data
		 */
		getCustomDataSelections (target) {
			let selections = [];
			if (target) {
				_.each(target.values, (value) => {
					const tooltip = value.payload && value.payload.tooltip;
					if (tooltip) {
						// append the name/value pairs
						if (tooltip.getLines) {
							const lines = tooltip.getLines();
							if (lines && lines.length > 0) {
								selections.push.apply(selections, tooltip.getLines());
							}
						}
						// only has generated string caption value without a name/value pair
						if (tooltip.getCaption) {
							const caption = tooltip.getCaption();
							if (caption) {
								selections.push({
									value: caption
								});
							}
						}
					}
				});
			}
			return selections;
		}

		/**
		 * Determine whether the given interactivity state should be handled
		 * @param {Object} state visualization interactivity state
		 * @return visualization interactivity state if it's supported, otherwise false
		 */
		_handleInteractivityState(state) {
			return VIPRUtils.isPropertyIncluded(this.visualization.getDefinition().getId(), 'interactivityState') ? state : undefined;
		}

		/**
		 * Determine whether the vipr visualization supports pan and zoom
		 */
		canZoom () {
			return this.viprInteractivity ? this.viprInteractivity.canZoom() : false;
		}

		/**
		 * Start the zooming session with the VIPR visualization
		 *
		 * @param event Event object
		 */
		zoomStart (event) {
			if (this.viprInteractivity) {
				try {
					// in case of a two touch gesture zoom, use the center coordinates of the hammer gesture event
					const coordinates = event.gesture ? event.gesture.center : event;
					var visCoord = this.viprWidget.getVisCoordinate(this._getClientX(coordinates), this._getClientY(coordinates));
					this.viprInteractivity.startInteractivity(visCoord);
					// initialize the gesture scale to 1 as we start zoom
					this.gestureScale = 1;
				} catch (err) {
					console.warn(err);
				}
			}
		}

		/**
		 * Apply zoom to the VIPR visualization
		 *
		 * @param event Event object
		 */
		zoom (event) {
			if (this.viprInteractivity) {
				try {
					// in case of a two touch gesture zoom, use the center coordinates of the hammer gesture event
					const coordinates = event.gesture ? event.gesture.center : event;
					var visCoord = this.viprWidget.getVisCoordinate(this._getClientX(coordinates), this._getClientY(coordinates));
					var scale = this._getZoomScale(event);
					this.viprInteractivity.zoom(visCoord, scale);
				} catch (err) {
					console.warn(err);
				}
			}
		}

		/**
		 * End the zooming session with the VIPR visualization
		 *
		 * @param event Event object
		 * @returns {Object|undefined} interactivity state containing the zoom level OR undefined if state is not supported
		 */
		zoomEnd (event) {
			if (this.viprInteractivity) {
				try {
					// in case of a two touch gesture zoom, use the center coordinates of the hammer gesture event
					const coordinates = event.gesture ? event.gesture.center : event;
					var visCoord = this.viprWidget.getVisCoordinate(this._getClientX(coordinates), this._getClientY(coordinates));
					this.viprInteractivity.endInteractivity(visCoord);
					// disable the gesture scale as we end zoom
					this.gestureScale = 0;
					return this._handleInteractivityState(this.viprWidget.getState());
				} catch (err) {
					console.warn(err);
				}
			}
		}

		/**
		 * Start the panning session with VIPR visualization
		 *
		 * @param event Event object
		 */
		panStart (event) {
			if (this.viprInteractivity) {
				try {
					var visCoord = this.viprWidget.getVisCoordinate(this._getClientX(event), this._getClientY(event));
					this.viprInteractivity.startInteractivity(visCoord);
				} catch (err) {
					console.warn(err);
				}
			}
		}

		/**
		 * Apply panning to the VIPR visualization
		 *
		 * @param event Event object
		 */
		pan (event) {
			if (this.viprInteractivity) {
				try {
					var visCoord = this.viprWidget.getVisCoordinate(this._getClientX(event), this._getClientY(event));
					this.viprInteractivity.translate(visCoord);
				} catch (err) {
					console.warn(err);
				}
			}
		}

		/**
		 * End the panning session with VIPR visualization
		 *
		 * @param event Event object
		 * @returns {Object|undefined} interactivity state containing the pan OR undefined if state is not supported
		 */
		panEnd (event) {
			if (this.viprInteractivity) {
				try {
					var visCoord = this.viprWidget.getVisCoordinate(this._getClientX(event), this._getClientY(event));
					this.viprInteractivity.endInteractivity(visCoord);
					return this._handleInteractivityState(this.viprWidget.getState());
				} catch (err) {
					console.warn(err);
				}
			}
		}

		processKeyDown () {
			return false;
		}

		/**
		 * @private
		 * @function _getClientX
		 * Get the clientX property from the given event
		 * @param {Object} event - the event object
		 * @returns clientX value of the event
		 */
		_getClientX (event) {
			return (event.hasOwnProperty('clientX')) ? event.clientX : event.originalEvent.clientX;
		}

		/**
		 * @private
		 * @function __getClientY
		 * Get the clientY property from the given event
		 * @param {Object} event - the event object
		 * @returns clientY value of the event
		 */
		_getClientY (event) {
			return (event.hasOwnProperty('clientY')) ? event.clientY : event.originalEvent.clientY;
		}

		_processItems (items, options){
			var targets;
			var summarize = options.summarize;
			var visCoord = options.visCoord;

			switch(this._getTargetType(items)) {
				case VIPRTYPE.DATAPOINT: {
					const shouldDataItemsSummarize = VIPRUtils.doesConfigPropertyMatchExpected(this.visualization.getDefinition().getId(), 'shouldMultiPointSelectionSummarize', true);
					targets = this._processDataPointTargets(items, summarize || shouldDataItemsSummarize);
					break;
				}
				case VIPRTYPE.VALUE:
					targets = this._processValueTargets(items);
					break;
				case VIPRTYPE.ITEM:
				case VIPRTYPE.TUPLE:
					targets = this._processTupleItemTargets(items);
					break;
				case VIPRTYPE.ITEMCLASS:
				case VIPRTYPE.DATAITEM:
					targets = this._processDataItemClassTargets(items);
					break;
				case VIPRTYPE.CUSTOMDATA:
					targets = this._processCustomDataTargets(items);
					break;
				case VIPRTYPE.EMPTY:
					targets = [];
					break;
					// invalid or unknown target
				case VIPRTYPE.INVALID:
				default:
					targets = null;
			}

			_.each(targets, (target) => {
				// Attach the clicked area to the target. The area could be of values: visualization, legend, or none
				if(visCoord && visCoord.area){
					target.area = visCoord.area;
					target.selectionContext = {
						actions: {
							actionOptions: { TextAction: { area: visCoord.area } }
						}
					};
				}
				// add datasetId;
				target.datasetId = this._datasetId(target);
			});

			return targets;
		}

		_getTargetType (items) {
			if (!items) {
				return VIPRTYPE.INVALID;
			}
			return items.length > 0 ? items[0].infoType : VIPRTYPE.EMPTY;
		}

		_processValueTargets (items) {
			let targets = [];
			_.each(items, (item) => {
				const valueObj = item.source;
				const slotIndex = valueObj.getIndex();

				// build a sparse values array
				// this allows re-using the existing datapoint mechanism with values
				let values = [];
				values[slotIndex] = [item.value];

				targets.push({
					key: 'value_' + slotIndex,
					type: VIPRTYPE.DATAPOINT,
					source: valueObj,
					values: values
				});
			});
			return targets;
		}

		_processDataPointTargets (items, summarize) {
			var targets = [];
			summarize = typeof summarize ===  'undefined' ? true : summarize;

			//The first slot in a selection is its topmost layer in the selection (the selectedLayer).
			//The datapoint target will be for that layer.
			var selectedLayer = null;
			_.each(items, (item) => {
				var slotAPIs = this._getSlotAPIsForItem(item);
				var slotLayer = (slotAPIs && slotAPIs[0].getDefinition().getDatasetIdList()[0]);
				if (!selectedLayer || slotLayer === selectedLayer) {
					selectedLayer = slotLayer;
					targets.push({
						key: item.key + ':' + slotLayer,
						type: VIPRTYPE.DATAPOINT,
						source: item.source,
						values: this._itemRowToTargetValues(item.row),
						slotAPIs: slotAPIs,
						onlyGlobalActions: this._isForecastData(item.source)
					});
				}
			});

			// Summarize multiple datapoint targets (area chart)

			if (targets.length && summarize) {
				//TODO: Temporarily wire of summarize dataPoint targets.
				targets = this.viprDataPointSummarizer.summarizeDataPointTargets(targets);
			}
			return targets;
		}

		_getSlotAPIsForItem (item) {
			let slotAPIs = (item.source &&
					item.source.dataset &&
					item.source.dataset.slotAPIs);
			if (!slotAPIs && item.source && item.source.dataset) {
				// the latest implementation of VIPRData does not supply the SlotAPIs
				// this is rather intentional, and can rather provide the id of the source dataset
				// @todo this needs to be revised so that the EventTargets simply returns a datasetId, not the list of SlotAPIs
				slotAPIs = SlotAPIHelper.getMappedSlotListByDataset(this.visualization, item.source.dataset.getId());
			}
			if (slotAPIs && this._isForecastData(item.source)) {
				return slotAPIs.map(	//Position of each slot matters, but we don't want numerical slots, so replace them with null
					slot => slot.getDataItemList().find(
						dataItem => dataItem.getType() === 'fact'
					) ? null : slot
				);
			}
			return slotAPIs;
		}

		_datasetId (item){
			return item && item.source &&  item.source.dataset && item.source.dataset.getId && item.source.dataset.getId();
		}
		//This function is called for both VIPRTYPE.ITEM and VIPRTYPE.TUPLE
		//VIPRTYPE.ITEM corresponds to elements such as axis labels (eg in a bar chart)
		//When axis labels are stacked, there will be n items of type VIPRTYPE.ITEM
		//VIPRTYPE.TUPLE corresponds to elements such as legend labels (type VIPRTYPE.TUPLE).
		_processTupleItemTargets (items) {
			var targets = [];
			_.each(items, (item) => {
				var source = item.source;
				let onlyGlobalActions = false;
				if (source.datapoints && source.datapoints.length > 0) {
					onlyGlobalActions = this._isForecastData(source.datapoints[0]) || false;
				}
				if (source) {
					targets.push({
						key: item.key || item.source.getUniqueName(),
						type: VIPRTYPE.ITEM,
						source: source,
						values: this.viprDataPointSummarizer.summarizeDataPointValues(source.datapoints, source.indexes.dataitem, source),
						isEdgeSelect: true,
						onlyGlobalActions: onlyGlobalActions
					});
				}
			});

			if (items.length > 1) {
				targets = this._mergeTupleItemTargets(targets);
			}
			return targets;
		}

		_processDataItemClassTargets (items) {
			var targets = [];
			_.each(items, (item) => {
				var values = [];
				var slotIds = _.map(this.visualization.getSlots().getMappedSlotList(), function(slot) { return slot.getId(); });
				var slotIndex = slotIds.indexOf(item.slotDef.name);
				if (slotIndex > -1) {
					values[slotIndex] = [{
						u: item.source.getUniqueName(),
						d: item.source.getCaption()
					}];

					targets.push({
						key: item.source.getUniqueName(),
						type: VIPRTYPE.ITEMCLASS,
						source: item.source,
						values: values
					});
				}
			});
			return targets;
		}

		_processCustomDataTargets (items) {
			var targets = [];
			_.each(items, (item) => {
				targets.push({
					key: item.id,
					type: item.infoType,
					source: {},
					values: [{ payload: item.payload, customType: item.type }]
				});
			});
			return targets;
		}

		_itemRowToTargetValues (row) {
			const targetValues = [];
			if (row){
				row.forEach((data, indexInRow) => {
					if (data.infoType === VIPRDATAPOINTTYPE.VALUE) {
						// Don't assume that the indexInRow is the same as the slot index
						// there could be a slot that contains multiple dataitems
						// We get the slot index from dataItem.indexes.dataitem
						let slotIndex = data.dataItem &&
							data.dataItem.source &&
							data.dataItem.source.indexes &&
							data.dataItem.source.indexes.dataitem;

						if (slotIndex == null) {
							// not sure if we would ever run into this.. but just in case we have scenarios where the slots index is not found
							slotIndex = indexInRow;
						}

						if (!targetValues[slotIndex]) {
							targetValues[slotIndex] = [];
						}
						const values = targetValues[slotIndex];

						const multiMeasureSeries = _.find(row, function (data) { return data.key && data.key.indexOf(SlotAPIHelper.MULTI_MEASURES_SERIES) === 0; });
						let index = multiMeasureSeries ? multiMeasureSeries.index : values.length;
						if (data.dataItem && data.dataItem.source instanceof VIPRCatContDataItem) {
							values[index] = {
								u: data.value,
								d: data.value
							};
						} else {
							// if possible, use the raw data
							values[index] = (data.source && data.source.getRawValue) ? data.source.getRawValue() : data.value;
						}
					} else {
						targetValues[indexInRow] = _.map(data.items, function (catItem) {
							return {
								u: catItem.source.item.u,
								d: catItem.source.item.d,
								p: catItem.source.item.p

							};
						});
					}
				});
			}


			return targetValues;
		}

		/**
		 * Merge the multiple nested tuple items to one target
		 */
		_mergeTupleItemTargets (prevTargets) {
			var params = [true, {}];
			return [$.extend.apply($, params.concat(prevTargets))];
		}

		_handleCustomDataDecorations (items, options) {
			var decorations = [];

			// Decorate all items found under the pointer
			_.chain(items).pluck('values').flatten().each(value => {
				if (_.contains(CUSTOMDATATYPE, value.customType)) {
					decorations.push({
						id: value.payload.id,
						value: true
					});
				}
			});

			this.visAPI.setCustomDataDecoration('selected', decorations, options);
		}

		/**
		 * Get the zoom scale from the event
		 */
		_getZoomScale (event) {
			if (event.gesture) {
				// default the gesture scale to 1 as we start zoom
				const previousScale = this.gestureScale || 1;
				// update the previous gesture scale
				this.gestureScale = event.gesture.scale;
				// calculate gesture zoom scale
				return event.gesture.scale / previousScale;
			} else {
				const deltaMode = event.originalEvent.deltaMode;
				let delta = event.originalEvent.deltaY;
				// FireFox case: deltaMode is always 1 for zoom in/out. Formula is the same as VIDA demo page
				if (deltaMode === 1) {
					delta = delta * 100 / 3;
				}

				// Follow VIDA demo to use deltaY. Formula used to calculate the scale is 1 - .002 * i, where i = event.deltaY
				return 1 - ZOOM_VELOCITY * delta;
			}
		}

		/**
		* Checks item source's to see if it contains forecast info
		* @param {Object} itemSource - target item.source
		* @return true if the target item is forecast data, otherwise false
		*/
		_isForecastData (itemSource) {
			return itemSource && itemSource.decorations && itemSource.decorations.forecast;
		}
	}

	return VIPREventTarget;
});
